/**
 * R-62 — keyword overlap topical drift detector.
 */
import { tokenizeSectionText } from '../docQuery';
import type { SectionDriftRegistryEntry } from './sectionDriftRegistry';

export interface KeywordDriftFinding {
  detector: 'keyword';
  reportTypeId: string;
  sectionId: string;
  severity: 'block' | 'warn';
  overlap: number;
  requiredKeywordCount: number;
  matchedKeywords: string[];
  missingKeywords: string[];
  prohibitedHits: string[];
  message: string;
}

function normalizeKeyword(keyword: string): string {
  return keyword.toLowerCase().trim();
}

function keywordInText(textLower: string, keyword: string): boolean {
  const k = normalizeKeyword(keyword);
  if (!k) return false;
  if (k.includes(' ')) return textLower.includes(k);
  const tokens = new Set(tokenizeSectionText(textLower, 512));
  return tokens.has(k) || textLower.includes(k);
}

export function detectKeywordDrift(
  sectionText: string,
  registryEntry: SectionDriftRegistryEntry,
): KeywordDriftFinding[] {
  const text = sectionText.trim();
  if (!text || text.length < 40) return [];

  const textLower = text.toLowerCase();
  const required = registryEntry.requiredKeywords.map(normalizeKeyword).filter(Boolean);
  if (required.length === 0) return [];

  const matchedKeywords: string[] = [];
  const missingKeywords: string[] = [];
  for (const kw of required) {
    const aliases = registryEntry.keywordAliases?.[kw] ?? [];
    if ([kw, ...aliases].some(term => keywordInText(textLower, term))) matchedKeywords.push(kw);
    else missingKeywords.push(kw);
  }

  const overlap = matchedKeywords.length / required.length;

  const prohibitedHits: string[] = [];
  for (const prohibited of registryEntry.prohibitedKeywords) {
    const k = normalizeKeyword(prohibited);
    if (!k) continue;
    if (k.includes(' ')) {
      let idx = 0;
      while (textLower.indexOf(k, idx) >= 0) {
        prohibitedHits.push(prohibited);
        idx = textLower.indexOf(k, idx) + k.length;
      }
    } else {
      const tokens = tokenizeSectionText(textLower, 512);
      const tokenCount = tokens.filter((t) => t === k).length;
      for (let i = 0; i < tokenCount; i += 1) prohibitedHits.push(prohibited);
      if (tokenCount === 0 && textLower.includes(k)) prohibitedHits.push(prohibited);
    }
  }

  let severity: 'block' | 'warn' | null = null;
  if (overlap < 0.2) severity = 'block';
  else if (overlap < 0.4) severity = 'warn';

  if (prohibitedHits.length >= 3 && (!severity || severity === 'warn')) {
    severity = 'warn';
  }

  if (!severity) return [];

  const message = severity === 'block'
    ? `Section "${registryEntry.sectionId}" keyword overlap ${(overlap * 100).toFixed(0)}% (below 20% threshold). Missing: ${missingKeywords.join(', ')}.`
    : `Section "${registryEntry.sectionId}" keyword overlap ${(overlap * 100).toFixed(0)}% or prohibited vocabulary (${prohibitedHits.length} hits).`;

  return [{
    detector: 'keyword',
    reportTypeId: registryEntry.reportTypeId,
    sectionId: registryEntry.sectionId,
    severity,
    overlap,
    requiredKeywordCount: required.length,
    matchedKeywords,
    missingKeywords,
    prohibitedHits,
    message,
  }];
}
